'use client';

import React, { useState } from 'react';
import { Check, ExternalLink, HelpCircle, ChevronDown, ChevronUp, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Platform configuration with URL patterns and help text
export const PLATFORMS = {
  google: {
    name: 'Google',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
    ),
    placeholder: 'https://g.page/r/YOUR_ID/review',
    inputLabel: 'Google review link',
    helpTitle: 'How to find your Google review link',
    helpSteps: [
      'Go to Google Business Profile Manager',
      'Select your business location',
      'Click "Get more reviews" or "Share review form"',
      'Copy the link provided'
    ],
    altMethod: 'Or search your business on Google Maps → Click "Write a review" → Copy the URL',
    validatePattern: /(g\.page|google\.com\/maps|search\.google\.com)/i,
    learnMoreUrl: 'https://support.google.com/business/answer/7035772'
  },
  trustpilot: {
    name: 'Trustpilot',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path fill="#00b67a" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
      </svg>
    ),
    placeholder: 'https://www.trustpilot.com/evaluate/yourcompany.com',
    inputLabel: 'Trustpilot review link',
    helpTitle: 'How to find your Trustpilot review link',
    helpSteps: [
      'Go to your Trustpilot business profile',
      'Your review link can be: trustpilot.com/evaluate/[your-domain] or trustpilot.com/review/[your-domain]',
      'Example: trustpilot.com/evaluate/smartmetrics.com'
    ],
    altMethod: 'Just replace "yourcompany.com" with your actual domain',
    validatePattern: /trustpilot\.com\/(evaluate|review)/i,
    learnMoreUrl: 'https://support.trustpilot.com/hc/en-us/articles/201839083'
  },
  yelp: {
    name: 'Yelp',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path fill="#FF1A1A" d="M12.14 9.27l-3.63-6.3C7.81 1.86 6.7 2.11 6.26 3.19c-.32.78-.19 3.27-.02 4.73.13 1.11.62 1.67 1.54 1.75l4.36.33zm-.28 2.33l-3.88 2.24c-.91.53-1.1 1.37-.58 2.27.37.65 2.37 2.87 3.27 3.67.68.61 1.38.53 1.94-.19l2.78-3.63c.5-.66.33-1.41-.43-1.79l-3.1-1.57zm2.08-1.1l4.08-1.15c.96-.27 1.28-.97 1-1.99-.2-.74-1.42-3.17-2.08-4.17-.5-.76-1.18-.86-1.86-.28L12.3 5.43c-.61.52-.61 1.29 0 1.81l1.64 1.26zm.8 3.06l1.65 4.07c.39.96 1.13 1.22 2.03.7.65-.37 2.37-2.06 3.08-2.93.54-.66.41-1.35-.32-1.84l-3.65-2.42c-.66-.44-1.38-.28-1.71.47l-.08.15-.5 1.8h-.5zm-4.89.47l-3.65 2.42c-.73.49-.86 1.18-.32 1.84.71.87 2.43 2.56 3.08 2.93.9.52 1.64.26 2.03-.7l1.65-4.07-.5-1.8-.08-.15c-.33-.75-1.05-.91-1.71-.47l-.5.5z"/>
      </svg>
    ),
    placeholder: 'https://www.yelp.com/writeareview/biz/your-business-city',
    inputLabel: 'Yelp review link',
    helpTitle: 'How to find your Yelp review link',
    helpSteps: [
      'Go to your Yelp business page',
      'Click "Write a Review" button',
      'Copy the URL from your browser'
    ],
    altMethod: 'The URL format is: yelp.com/writeareview/biz/[business-name-city]',
    validatePattern: /yelp\.com\/(writeareview|biz)/i,
    learnMoreUrl: 'https://business.yelp.com/'
  },
  facebook: {
    name: 'Facebook',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    placeholder: 'https://www.facebook.com/yourpage/reviews',
    inputLabel: 'Facebook page URL',
    helpTitle: 'How to find your Facebook reviews link',
    helpSteps: [
      'Go to your Facebook Business Page',
      'Click on the "Reviews" tab',
      'Copy the URL from your browser'
    ],
    altMethod: 'Or just paste your Facebook page URL - we\'ll link to the reviews tab',
    validatePattern: /facebook\.com/i,
    learnMoreUrl: 'https://www.facebook.com/business/help'
  },
  g2: {
    name: 'G2',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <circle fill="#FF492C" cx="12" cy="12" r="10"/>
        <text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">G2</text>
      </svg>
    ),
    placeholder: 'https://www.g2.com/products/your-product/reviews',
    inputLabel: 'G2 product page URL',
    helpTitle: 'How to find your G2 review link',
    helpSteps: [
      'Go to g2.com and search for your product',
      'Navigate to your product page',
      'Copy the URL and add "/reviews" at the end'
    ],
    altMethod: 'Format: g2.com/products/[your-product]/reviews',
    validatePattern: /g2\.com\/products/i,
    learnMoreUrl: 'https://sell.g2.com/'
  },
  capterra: {
    name: 'Capterra',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <rect fill="#FF9D28" width="24" height="24" rx="4"/>
        <text x="12" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">CAP</text>
      </svg>
    ),
    placeholder: 'https://reviews.capterra.com/products/new/...',
    inputLabel: 'Capterra review link',
    helpTitle: 'How to find your Capterra review link',
    helpSteps: [
      'Go to capterra.com and search for your product',
      'Navigate to your product listing page',
      'Copy the review URL (may be reviews.capterra.com or capterra.com)'
    ],
    altMethod: 'Review links can be from reviews.capterra.com or capterra.com',
    validatePattern: /(reviews\.)?capterra\.com/i,
    learnMoreUrl: 'https://www.capterra.com/vendors/'
  },
  trustradius: {
    name: 'TrustRadius',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <circle fill="#12344D" cx="12" cy="12" r="10"/>
        <text x="12" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">TR</text>
      </svg>
    ),
    placeholder: 'https://www.trustradius.com/products/your-product/reviews',
    inputLabel: 'TrustRadius product page URL',
    helpTitle: 'How to find your TrustRadius review link',
    helpSteps: [
      'Go to trustradius.com and search for your product',
      'Navigate to your product page',
      'Copy the URL from your browser'
    ],
    altMethod: 'Format: trustradius.com/products/[your-product]/reviews',
    validatePattern: /trustradius\.com/i,
    learnMoreUrl: 'https://www.trustradius.com/vendor'
  },
  clutch: {
    name: 'Clutch',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5">
        <circle fill="#E94E1B" cx="12" cy="12" r="10"/>
        <text x="12" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">C</text>
      </svg>
    ),
    placeholder: 'https://clutch.co/profile/your-company',
    inputLabel: 'Clutch profile URL',
    helpTitle: 'How to find your Clutch review link',
    helpSteps: [
      'Go to clutch.co and search for your company',
      'Navigate to your company profile',
      'Copy the URL from your browser'
    ],
    altMethod: 'Format: clutch.co/profile/[your-company-name]',
    validatePattern: /clutch\.co/i,
    learnMoreUrl: 'https://clutch.co/agencies'
  }
};

interface PlatformCardProps {
  platformKey: string;
  savedUrl?: string;
  onSave: (platformKey: string, url: string) => void;
  onRemove: (platformKey: string) => void;
  onCancel?: () => void;
}

function PlatformCard({ platformKey, savedUrl, onSave, onRemove, onCancel }: PlatformCardProps) {
  const [url, setUrl] = useState(savedUrl || '');
  const [showHelp, setShowHelp] = useState(false);
  const [error, setError] = useState('');

  const platform = PLATFORMS[platformKey as keyof typeof PLATFORMS];

  const validateUrl = (value: string) => {
    if (!value) return true;
    return platform.validatePattern.test(value);
  };

  const handleSave = () => {
    const trimmedUrl = url.trim();
    
    // If empty, remove from configured platforms
    if (!trimmedUrl) {
      if (savedUrl) {
        onRemove(platformKey);
      } else if (onCancel) {
        onCancel();
      }
      return;
    }
    
    // Validate non-empty URLs
    if (!validateUrl(trimmedUrl)) {
      setError(`This doesn't look like a valid ${platform.name} URL`);
      return;
    }
    
    setError('');
    onSave(platformKey, trimmedUrl);
  };

  const handleTest = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-5 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
          {platform.icon}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">{platform.name}</h3>
          <p className="text-xs text-slate-500">{platform.inputLabel}</p>
        </div>
        {savedUrl && (
          <button
            onClick={() => onRemove(platformKey)}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Remove this platform"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Input Section */}
      <div className="space-y-3">
        <div>
          <Input
            id={`${platformKey}-url`}
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setError('');
            }}
            placeholder={platform.placeholder}
            className={error ? 'border-red-300 bg-red-50' : ''}
            autoFocus
          />
          {error && (
            <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
        </div>

        {/* Help Toggle */}
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span>{platform.helpTitle}</span>
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {/* Help Content */}
        {showHelp && (
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <ol className="list-decimal list-inside space-y-1 text-slate-600">
              {platform.helpSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {platform.altMethod && (
              <p className="mt-2 text-slate-500 text-xs italic">{platform.altMethod}</p>
            )}
            <a
              href={platform.learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-indigo-600 hover:text-indigo-700 text-xs"
            >
              Learn more <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            onClick={handleSave}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {url.trim() ? (savedUrl ? 'Update' : 'Save') : 'Skip'}
          </Button>
          {url.trim() && (
            <Button
              onClick={handleTest}
              variant="outline"
              className="flex items-center gap-1.5"
            >
              <ExternalLink className="w-4 h-4" />
              Test
            </Button>
          )}
          <Button
            onClick={handleCancel}
            variant="ghost"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

interface PlatformSettingsProps {
  platforms: Record<string, string>;
  onChange: (platforms: Record<string, string>) => void;
}

export default function PlatformSettings({ platforms, onChange }: PlatformSettingsProps) {
  const [editingPlatform, setEditingPlatform] = useState<string | null>(null);

  const handleSave = (platformKey: string, url: string) => {
    onChange({ ...platforms, [platformKey]: url });
  };

  const handleRemove = (platformKey: string) => {
    const updated = { ...platforms };
    delete updated[platformKey];
    onChange(updated);
  };

  const allPlatformKeys = Object.keys(PLATFORMS);

  return (
    <div className="space-y-4">
      

      {/* Platform Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {allPlatformKeys.map(platformKey => {
          const platform = PLATFORMS[platformKey as keyof typeof PLATFORMS];
          const isConfigured = !!platforms[platformKey];

          return (
            <button
              key={platformKey}
              onClick={() => setEditingPlatform(platformKey)}
              className={`relative p-4 border-2 rounded-xl transition-all hover:scale-105 ${
                isConfigured
                  ? 'border-green-400 bg-green-50 hover:border-green-500'
                  : 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
              }`}
            >
              <div className="flex flex-col items-center gap-2">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  isConfigured ? 'bg-green-100' : 'bg-slate-100'
                }`}>
                  {platform.icon}
                </div>
                <span className="text-sm font-medium text-slate-700">{platform.name}</span>
                {isConfigured && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Edit Form (shown when a platform is selected) */}
      {editingPlatform && (
        <div className="mt-4">
          <PlatformCard
            key={editingPlatform}
            platformKey={editingPlatform}
            savedUrl={platforms[editingPlatform]}
            onSave={(key, url) => {
              handleSave(key, url);
              setEditingPlatform(null);
            }}
            onRemove={(key) => {
              handleRemove(key);
              setEditingPlatform(null);
            }}
            onCancel={() => setEditingPlatform(null)}
          />
        </div>
      )}
    </div>
  );
}

